import React from 'react';

import { TimeReportKpiTile } from '../time_report/TimeReportKpis';

import { formatMoney, formatPercent, ZERO_DASH } from './budgetOverviewFormat';
import BudgetOverviewInfoTip from './BudgetOverviewInfoTip';

function Title({ definition }) {
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
            {definition.label}
            <BudgetOverviewInfoTip label={definition.label} text={definition.text} />
        </span>
    );
}

/**
 * The four headline figures, read from the total row so a card can never
 * disagree with the table beneath it.
 */
export default function BudgetOverviewKpis({ report, definitions, committedActuals, locale }) {
    const totals = report?.totals;

    if (!totals) {
        return (
            <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
                {['budget', 'markups', 'total', 'margin'].map((key) => (
                    <TimeReportKpiTile key={key} title={<Title definition={definitions[key]} />} value={ZERO_DASH} />
                ))}
            </div>
        );
    }

    // In a chosen currency the cards cover the projects with a rate, and say
    // which currency they are in.
    const fx = report.fx || null;
    const count = fx ? report.projects.filter((project) => !project.fx?.missing).length : report.projects.length;
    const money = (value) =>
        fx ? (
            <>
                {formatMoney(value, locale)}
                <span className="tw-ms-1 tw-text-xs tw-font-medium tw-text-charcoal-50">{fx.show_in}</span>
            </>
        ) : (
            formatMoney(value, locale)
        );
    const markupOnBudget = totals.budget ? (totals.markups / totals.budget) * 100 : null;

    const totalContext = report.mixed_currency ? 'mixed currencies, no conversion' : 'budget plus mark-ups';

    const marginContext = committedActuals ? (
        <>
            <span className={totals.remaining < 0 ? 'tw-text-red' : undefined}>{formatMoney(totals.remaining, locale)}</span> of budget remaining after
            committed and actuals
        </>
    ) : (
        definitions.margin.text.replace(/\.$/, '').toLowerCase()
    );

    return (
        <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
            <TimeReportKpiTile
                title={<Title definition={definitions.budget} />}
                value={money(totals.budget)}
                context={count === 1 ? 'across 1 project' : `across ${count} projects`}
            />
            <TimeReportKpiTile
                title={<Title definition={definitions.markups} />}
                value={money(totals.markups)}
                context={markupOnBudget === null ? null : `${formatPercent(markupOnBudget, locale)} mark-up on budget`}
            />
            <TimeReportKpiTile title={<Title definition={definitions.total} />} value={money(totals.total)} context={totalContext} />
            <TimeReportKpiTile
                title={<Title definition={definitions.margin} />}
                value={formatPercent(totals.margin_pct, locale)}
                valueClassName={totals.margin_value < 0 ? 'tw-text-red' : 'tw-text-green'}
                context={marginContext}
            />
        </div>
    );
}
